import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors + normalise FastAPI 422 validation errors so `error.response.data.detail`
// never contains an array of objects (which would crash `toast.error(...)` when passed to React).
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    const detail = error.response?.data?.detail;
    if (Array.isArray(detail)) {
      // Pydantic v2 style: [{type, loc, msg, ...}, ...]
      error.response.data.detail = detail
        .map((d) => (typeof d === 'string' ? d : (d?.msg || JSON.stringify(d))))
        .join('; ');
    } else if (detail && typeof detail === 'object') {
      error.response.data.detail = detail.msg || JSON.stringify(detail);
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
};

// Companies API
export const companiesAPI = {
  getAll: () => api.get('/companies'),
  getById: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  uploadLogo: (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/companies/${id}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteLogo: (id) => api.delete(`/companies/${id}/logo`),
  uploadSignature: (id, file, kind) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/companies/${id}/signature?kind=${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  updateLetterhead: (id, data) => api.put(`/companies/${id}/letterhead`, data),
  logoUrl: (id) => `${api.defaults.baseURL}/companies/${id}/logo?ts=${Date.now()}`,
  signatureUrl: (id, kind = 'signature') => `${api.defaults.baseURL}/companies/${id}/signature?kind=${kind}&ts=${Date.now()}`,
};

// HR Document Generators API
export const hrDocsAPI = {
  generateOfferLetter: (data) => api.post('/documents/offer-letter', data, { responseType: 'blob' }),
  generateSalaryCertificate: (data) => api.post('/documents/salary-certificate', data, { responseType: 'blob' }),
  generateHR: (data) => api.post('/documents/hr', data, { responseType: 'blob' }),
  types: () => api.get('/documents/types'),
  list: (params) => api.get('/documents/generated', { params }),
  download: (id) => api.get(`/documents/generated/${id}/download`, { responseType: 'blob' }),
};

// Resignations / Exit Management API
export const exitAPI = {
  submit: (data) => api.post('/me/resignation', data),
  mine: () => api.get('/me/resignation'),
  withdraw: (id) => api.delete(`/me/resignation/${id}`),
  list: (params) => api.get('/resignations', { params }),
  decide: (id, decision) => api.put(`/resignations/${id}/decision`, decision),
  terminate: (employeeId, data) => api.post(`/employees/${employeeId}/terminate`, data),
  reactivate: (employeeId) => api.post(`/employees/${employeeId}/reactivate`),
};

// Offboarding API (Phase 10b)
export const offboardingAPI = {
  list: (params) => api.get('/offboarding', { params }),
  get: (employeeId) => api.get(`/offboarding/${employeeId}`),
  updateTask: (employeeId, data) => api.put(`/offboarding/${employeeId}/task`, data),
  submitExitInterview: (employeeId, data) => api.post(`/offboarding/${employeeId}/exit-interview`, data),
  uploadKT: (employeeId, file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/offboarding/${employeeId}/kt-upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  ktDownloadUrl: (employeeId, fileId) => `${api.defaults.baseURL}/offboarding/${employeeId}/kt-download/${fileId}`,
  calculateFF: (employeeId, data) => api.post(`/offboarding/${employeeId}/ff-calculate`, data),
  downloadFFPdf: (employeeId) => api.get(`/offboarding/${employeeId}/ff-pdf`, { responseType: 'blob' }),
  // F&F Approval workflow
  ffWorkflow: (employeeId) => api.get(`/offboarding/${employeeId}/ff-workflow`),
  ffPropose: (employeeId, data) => api.post(`/offboarding/${employeeId}/ff-propose`, data || {}),
  ffDecide: (employeeId, data) => api.post(`/offboarding/${employeeId}/ff-decide`, data),
  ffFinalize: (employeeId, data) => api.post(`/offboarding/${employeeId}/ff-finalize`, data || {}),
  ffMarkPaid: (employeeId, data) => api.post(`/offboarding/${employeeId}/ff-mark-paid`, data),
  ffWithdraw: (employeeId, data) => api.post(`/offboarding/${employeeId}/ff-withdraw`, data || {}),
};

// Inter-company transfer (Phase 10c)
export const transferAPI = {
  transfer: (employeeId, data) => api.post(`/employees/${employeeId}/transfer`, data),
  history: (employeeId) => api.get(`/employees/${employeeId}/transfer-history`),
};

// Employee live profile details (aggregated)
export const employeeAdminAPI = {
  getFull: (employeeId) => api.get(`/admin/employees/${employeeId}/full`),
  fullEdit: (employeeId, data) => api.put(`/admin/employees/${employeeId}/full-edit`, data),
  history: (employeeId) => api.get(`/admin/employees/${employeeId}/edit-history`),
};

export const employeeProfileAPI = {
  details: (employeeId) => api.get(`/employees/${employeeId}/profile-details`),
};

// Phase 12a — Advanced Leave Management
export const leaveAdvancedAPI = {
  listTypesMaster: () => api.get('/leave-types-master'),
  seedDefaults: (companyId) => api.post('/leave-policies/seed-defaults', null, { params: companyId ? { company_id: companyId } : {} }),
  listPolicies: (params) => api.get('/leave-policies', { params }),
  createPolicy: (data) => api.post('/leave-policies', data),
  updatePolicy: (id, data) => api.put(`/leave-policies/${id}`, data),
  deletePolicy: (id) => api.delete(`/leave-policies/${id}`),
  applicablePolicy: (employeeId, leaveCode) => api.get('/leave-policies/applicable', { params: { employee_id: employeeId, leave_code: leaveCode } }),
  balanceV2: (employeeId, year) => api.get(`/employees/${employeeId}/leave-balance-v2`, { params: year ? { year } : {} }),
  createLeaveV2: (data) => api.post('/leaves/v2', data),
  teamConflicts: (employeeId, startDate, endDate) => api.get('/leaves/v2/team-conflicts', { params: { employee_id: employeeId, start_date: startDate, end_date: endDate } }),
  chronicPattern: (leaveId) => api.get(`/leaves/${leaveId}/chronic-pattern`),
};

// Data Migration / Go-Live Import (Phase 11 — Super Admin only)
export const migrationAPI = {
  types: () => api.get('/migration/types'),
  templateUrl: (type) => `${api.defaults.baseURL}/migration/template/${type}`,
  downloadTemplate: (type) => api.get(`/migration/template/${type}`, { responseType: 'blob' }),
  upload: (formData) => api.post('/migration/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  apply: (batchId, params) => api.post(`/migration/batches/${batchId}/import`, null, { params }),
  list: (params) => api.get('/migration/batches', { params }),
  get: (batchId) => api.get(`/migration/batches/${batchId}`),
  errorReport: (batchId) => api.get(`/migration/batches/${batchId}/error-report`, { responseType: 'blob' }),
  sourceFile: (batchId) => api.get(`/migration/batches/${batchId}/source`, { responseType: 'blob' }),
  rollback: (batchId) => api.post(`/migration/batches/${batchId}/rollback`),
};

// Employees API
export const employeesAPI = {
  getAll: (params) => api.get('/employees', { params }),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  assignManager: (id, managerId) => api.put(`/employees/${id}/manager`, { manager_id: managerId || null }),
  // One-click user account provisioning
  getUserAccount: (id) => api.get(`/employees/${id}/user-account`),
  createUserAccount: (id, data = {}) => api.post(`/employees/${id}/user-account`, data),
  bulkCreateUserAccounts: (data) => api.post('/employees/bulk/user-accounts', data),
  userAccountStatusMap: () => api.get('/employees/user-accounts/status'),
  resetUserPassword: (id) => api.post(`/employees/${id}/user-account/reset-password`),
  resendWelcome: (id) => api.post(`/employees/${id}/user-account/resend-welcome`),
  toggleUserAccount: (id, activate) => api.post(`/employees/${id}/user-account/toggle-active`, null, { params: { activate } }),
};

// Attendance API
export const attendanceAPI = {
  punch: (data) => api.post('/attendance/punch', data),
  getAll: (params) => api.get('/attendance', { params }),
  getTodaySummary: (companyId) => api.get('/attendance/today-summary', { params: { company_id: companyId } }),
  getSettings: () => api.get('/admin/attendance-settings'),
  updateSettings: (data) => api.put('/admin/attendance-settings', data),
};

// Leave API
export const leaveAPI = {
  getTypes: (country) => api.get('/leave-types', { params: { country } }),
  createType: (data) => api.post('/leave-types', data),
  getAll: (params) => api.get('/leaves', { params }),
  create: (data) => api.post('/leaves', data),
  approve: (id, stage) => api.put(`/leaves/${id}/approve`, null, { params: stage ? { stage } : {} }),
  reject: (id, reason) => api.put(`/leaves/${id}/reject`, null, { params: { reason } }),
  getBalance: (employeeId) => api.get(`/leaves/balance/${employeeId}`),
};

// Expense API
export const expenseAPI = {
  getAll: (params) => api.get('/expenses', { params }),
  create: (data) => api.post('/expenses', data),
  approve: (id, stage) => api.put(`/expenses/${id}/approve`, null, { params: stage ? { stage } : {} }),
  reject: (id, reason) => api.put(`/expenses/${id}/reject`, null, { params: reason ? { reason } : {} }),
  markPaid: (id) => api.put(`/expenses/${id}/mark-paid`),
};

// Payroll API
export const payrollAPI = {
  process: (data) => api.post('/payroll/process', data),
  getPayslips: (params) => api.get('/payroll/payslips', { params }),
  getPayslip: (id) => api.get(`/payroll/payslips/${id}`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentActivities: (limit = 10) => api.get('/dashboard/recent-activities', { params: { limit } }),
};

// Geofence API
export const geofenceAPI = {
  getAll: (companyId) => api.get('/geofences', { params: { company_id: companyId } }),
  create: (data) => api.post('/geofences', data),
  validate: (lat, lng, companyId) => api.post('/geofences/validate', null, { 
    params: { latitude: lat, longitude: lng, company_id: companyId } 
  }),
};

// Notifications API
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
};

// Seed API
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// Onboarding API
export const onboardingAPI = {
  getMine: () => api.get('/onboarding/me'),
  updateMine: (data) => api.put('/onboarding/me', data),
  submit: () => api.post('/onboarding/submit'),
  list: (status) => api.get('/onboarding', { params: { status } }),
  approve: (employeeId, data) => api.post(`/onboarding/${employeeId}/approve`, data),
};

// Documents API
export const documentsAPI = {
  upload: (employeeId, formData) => api.post(`/employees/${employeeId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  list: (employeeId) => api.get(`/employees/${employeeId}/documents`),
  download: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
  verify: (id, decision, remarks) => api.put(`/documents/${id}/verify`, null, {
    params: { decision, remarks }
  }),
  delete: (id) => api.delete(`/documents/${id}`),
  expiring: (days = 90) => api.get('/documents/expiring', { params: { days } }),
};

// Photo API
export const photoAPI = {
  upload: (employeeId, formData) => api.post(`/employees/${employeeId}/photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (employeeId) => api.delete(`/employees/${employeeId}/photo`),
};

// Holiday/Calendar API
export const calendarAPI = {
  listHolidays: (params) => api.get('/holidays', { params }),
  createHoliday: (data) => api.post('/holidays', data),
  deleteHoliday: (id) => api.delete(`/holidays/${id}`),
  getEvents: (startDate, endDate, country) => api.get('/calendar/events', {
    params: { start_date: startDate, end_date: endDate, country }
  }),
};

// User Admin API
export const userAdminAPI = {
  list: () => api.get('/admin/users'),
  create: (data) => api.post('/admin/users', data),
  update: (id, data) => api.put(`/admin/users/${id}`, data),
  resetPassword: (id, data) => api.post(`/admin/users/${id}/reset-password`, data),
  lock: (id) => api.post(`/admin/users/${id}/lock`),
  unlock: (id) => api.post(`/admin/users/${id}/unlock`),
};

// Audit Log API
export const auditAPI = {
  list: (params) => api.get('/audit-logs', { params }),
};

// Device Trust API (Enterprise Device Verification)
export const deviceTrustAPI = {
  // Config
  configSelf: () => api.get('/device-trust/config'),
  configCompany: (companyId) => api.get(`/device-trust/config/${companyId}`),
  updateConfig: (companyId, data) => api.put(`/device-trust/config/${companyId}`, data),
  // Registration + verification
  register: (data) => api.post('/devices/register', data),
  verify: (data) => api.post('/devices/verify', data),
  agentRegister: (data) => api.post('/devices/agent-register', data),
  // Listing
  mine: () => api.get('/devices/mine'),
  list: (params) => api.get('/devices', { params }),
  pending: () => api.get('/devices/pending'),
  get: (id) => api.get(`/devices/${id}`),
  // Actions
  approve: (id, note) => api.post(`/devices/${id}/approve`, { note: note || null }),
  block: (id, note) => api.post(`/devices/${id}/block`, { note: note || null }),
  revoke: (id, note) => api.post(`/devices/${id}/revoke`, { note: note || null }),
  // Reports
  report: () => api.get('/device-trust/report'),
};

// Help / Manuals
export const helpAPI = {
  listManuals: () => api.get('/help/manuals'),
  getManual: (role) => api.get(`/help/manuals/${role}`),
  pdfUrl: (role) => `${api.defaults.baseURL}/help/manuals/${role}/pdf`,
};

// Phase 12b — Holidays API
export const holidaysAPI = {
  list: (params) => api.get('/holidays', { params }),
  effective: (year) => api.get('/holidays/effective', { params: year ? { year } : {} }),
  create: (data) => api.post('/holidays', data),
  update: (id, data) => api.put(`/holidays/${id}`, data),
  delete: (id) => api.delete(`/holidays/${id}`),
  bulkImport: (holidays) => api.post('/holidays/bulk-import', { holidays }),
};

// Phase 12b — Workflow Builder API
export const workflowAPI = {
  listTemplates: (params) => api.get('/workflow-templates', { params }),
  createTemplate: (data) => api.post('/workflow-templates', data),
  updateTemplate: (id, data) => api.put(`/workflow-templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/workflow-templates/${id}`),
  startInstance: (data) => api.post('/workflow-instances', data),
  getInstance: (id) => api.get(`/workflow-instances/${id}`),
  myPending: () => api.get('/workflow-instances/pending-for-me'),
  action: (id, action, note) => api.post(`/workflow-instances/${id}/action`, { action, note: note || null }),
};

// Phase 12b — WFH API
export const wfhAPI = {
  create: (data) => api.post('/wfh-requests', data),
  mine: () => api.get('/wfh-requests/mine'),
  team: (status) => api.get('/wfh-requests/team', { params: status ? { status } : {} }),
  listAll: (params) => api.get('/wfh-requests', { params }),
  approve: (id, note, stage) => api.put(`/wfh-requests/${id}/approve`, { note: note || null, stage: stage || null }),
  reject: (id, note) => api.put(`/wfh-requests/${id}/reject`, { note: note || null }),
  cancel: (id) => api.put(`/wfh-requests/${id}/cancel`),
};

// OD API (On Duty / Outdoor Duty)
export const odAPI = {
  create: (data) => api.post('/od-requests', data),
  mine: () => api.get('/od-requests/mine'),
  team: (status) => api.get('/od-requests/team', { params: status ? { status } : {} }),
  listAll: (params) => api.get('/od-requests', { params }),
  approve: (id, note, stage) => api.put(`/od-requests/${id}/approve`, { note: note || null, stage: stage || null }),
  reject: (id, note) => api.put(`/od-requests/${id}/reject`, { note: note || null }),
  cancel: (id) => api.put(`/od-requests/${id}/cancel`),
};

// Phase 12b — Leave Analytics API
export const leaveAnalyticsAPI = {
  utilization: (params) => api.get('/leave-analytics/utilization', { params }),
  absenteeism: (params) => api.get('/leave-analytics/absenteeism', { params }),
  lopTrend: (params) => api.get('/leave-analytics/lop-trend', { params }),
  forecast: (params) => api.get('/leave-analytics/forecast', { params }),
  exportUrl: () => `${api.defaults.baseURL}/leave-analytics/export`,
};

// Assets API
export const assetsAPI = {
  list: (params) => api.get('/assets', { params }),
  create: (data) => api.post('/assets', data),
  assign: (id, data) => api.post(`/assets/${id}/assign`, data),
  return: (id, data) => api.post(`/assets/${id}/return`, data),
  delete: (id) => api.delete(`/assets/${id}`),
  myAssets: () => api.get('/me/assets'),
};

// Tickets API
export const ticketsAPI = {
  list: (params) => api.get('/tickets', { params }),
  get: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  comment: (id, message) => api.post(`/tickets/${id}/comment`, { message }),
  update: (id, data) => api.put(`/tickets/${id}`, data),
};

// Training API
export const trainingAPI = {
  list: (params) => api.get('/training', { params }),
  create: (data) => api.post('/training', data),
  update: (id, data) => api.put(`/training/${id}`, data),
  delete: (id) => api.delete(`/training/${id}`),
  mine: () => api.get('/me/training'),
};

// Performance Reviews API
export const performanceAPI = {
  list: (params) => api.get('/performance-reviews', { params }),
  create: (data) => api.post('/performance-reviews', data),
  mine: () => api.get('/me/performance-reviews'),
};

// Analytics API
export const analyticsAPI = {
  overview: () => api.get('/analytics/overview'),
};

// Reminders API
export const remindersAPI = {
  list: (params) => api.get('/reminders', { params }),
  upcoming: (days = 30) => api.get('/reminders/upcoming', { params: { days } }),
  create: (data) => api.post('/reminders', data),
  acknowledge: (id) => api.put(`/reminders/${id}/acknowledge`),
  dismiss: (id) => api.put(`/reminders/${id}/dismiss`),
  delete: (id) => api.delete(`/reminders/${id}`),
};

// Email Config / SMTP / Templates API
export const emailAPI = {
  getConfig: () => api.get('/admin/email-config'),
  updateConfig: (data) => api.put('/admin/email-config', data),
  sendTest: (to) => api.post('/admin/email-config/test', { to }),
  listTemplates: () => api.get('/admin/email-templates'),
  updateTemplate: (data) => api.put('/admin/email-templates', data),
  resetTemplate: (event_key) => api.delete(`/admin/email-templates/${event_key}`),
};

// Shift Management API
export const shiftsAPI = {
  list: (params) => api.get('/shifts', { params }),
  create: (data) => api.post('/shifts', data),
  update: (id, data) => api.put(`/shifts/${id}`, data),
  delete: (id) => api.delete(`/shifts/${id}`),
  assign: (id, employee_ids) => api.post(`/shifts/${id}/assign`, { employee_ids }),
  unassign: (employee_id) => api.delete(`/employees/${employee_id}/shift`),
  mine: () => api.get('/me/shift'),
};

// Geofence Management API (extended — geofenceAPI above stays for backward compat)
export const geofenceAdminAPI = {
  list: (companyId) => api.get('/geofences', { params: companyId ? { company_id: companyId } : {} }),
  create: (data) => api.post('/geofences', data),
  update: (id, data) => api.put(`/geofences/${id}`, data),
  delete: (id) => api.delete(`/geofences/${id}`),
};

export default api;
